import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import SendIcon from '@mui/icons-material/Send';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import SectionHeader from '../../../components/shared/SectionHeader';
import { useToast } from '../../../components/shared/Toast';
import loansApi from '../../../services/loansApi';
import { getAxiosErrorMessage } from '../../../utils/getAxiosErrorMessage';
import {
  collectOriginationChargesFees,
  docOrderFieldPinId,
  isPlaceholderDash,
  processDocOrderSections,
  stripLeadingDisplayJunk,
} from '../../../utils/docOrderDisplayUtils';
import loanClosingCostsApi from '../../../services/loanClosingCostsApi';

const FieldValue = ({ value }) => {
  const placeholder = isPlaceholderDash(value);
  return (
    <Typography
      component="div"
      variant="body2"
      sx={{
        whiteSpace: 'pre-wrap',
        color: placeholder ? 'text.secondary' : 'text.primary',
      }}
    >
      {placeholder ? '—' : value}
    </Typography>
  );
};

/**
 * Interactive doc order review: pin fields for correction, add notes, submit to docrequest@uff.loans.
 */
const DocOrderRequestPanel = ({ docOrder, loan, loanId }) => {
  const { showToast, ToastContainer } = useToast();
  const [submitDocOrderRequest, { isLoading: submitting }] =
    loansApi.endpoints.submitDocOrderRequest.useMutation();

  const { data: closingCosts, isLoading: closingLoading } =
    loanClosingCostsApi.endpoints.getLoanClosingCosts.useQuery(loanId, { skip: !loanId });

  const originationFeeRows = useMemo(
    () => collectOriginationChargesFees(closingCosts),
    [closingCosts],
  );

  const processedSections = useMemo(
    () => processDocOrderSections(docOrder, loan, originationFeeRows),
    [docOrder, loan, originationFeeRows],
  );

  /** pinId -> { requestedValue, note } */
  const [pins, setPins] = useState({});
  const [generalNotes, setGeneralNotes] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const pinList = useMemo(() => {
    const items = [];
    for (const section of processedSections) {
      for (const field of section.fields || []) {
        const pinId = docOrderFieldPinId(section.sectionKey, field.key);
        const pin = pins[pinId];
        if (!pin) continue;
        const hasContent =
          (pin.requestedValue && pin.requestedValue.trim()) ||
          (pin.note && pin.note.trim());
        if (!hasContent) continue;
        items.push({
          pinId,
          sectionKey: section.sectionKey,
          sectionTitle: section.sectionTitle,
          field,
          ...pin,
        });
      }
    }
    return items;
  }, [processedSections, pins]);

  const togglePin = useCallback((pinId) => {
    setPins((prev) => {
      if (prev[pinId]) {
        const next = { ...prev };
        delete next[pinId];
        return next;
      }
      return { ...prev, [pinId]: { requestedValue: '', note: '' } };
    });
  }, []);

  const updatePin = useCallback((pinId, patch) => {
    setPins((prev) => ({
      ...prev,
      [pinId]: { ...prev[pinId], ...patch },
    }));
  }, []);

  const buildCorrectionsPayload = useCallback(() => {
    return pinList.map((item) => ({
      sectionKey: item.sectionKey,
      sectionTitle: stripLeadingDisplayJunk(item.sectionTitle),
      fieldKey: item.field.key,
      fieldLabel: stripLeadingDisplayJunk(item.field.label),
      listedValue: String(item.field.value ?? ''),
      requestedValue: (item.requestedValue || '').trim(),
      note: (item.note || '').trim(),
    }));
  }, [pinList]);

  const handleSubmit = async () => {
    setConfirmOpen(false);
    try {
      await submitDocOrderRequest({
        loanId,
        body: {
          intent: 'order_documents',
          generalNotes: generalNotes.trim(),
          corrections: buildCorrectionsPayload(),
        },
      }).unwrap();
      showToast(
        'Doc order request sent to UFF. You will receive a copy at your email on file.',
        'success',
      );
      setPins({});
      setGeneralNotes('');
    } catch (err) {
      showToast(getAxiosErrorMessage(err, 'Failed to submit doc order request.'), 'error');
    }
  };

  if (!docOrder) {
    return (
      <Typography variant="body2" color="text.secondary">
        No doc order information is available for this loan.
      </Typography>
    );
  }

  if (processedSections.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No doc order fields were returned.
      </Typography>
    );
  }

  return (
    <Box>
      {ToastContainer}
      {closingLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="caption" color="text.secondary">
            Loading closing cost lines…
          </Typography>
        </Box>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Review each line below. Use the pin on any row to flag a correction (what the value should be).
        When ready, submit — your request is emailed to the doc order team; you are copied on the message.
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={pinList.length > 0 ? 8 : 12}>
          {processedSections.map((section, sectionIdx) => (
            <Card key={`${section.sectionKey}-${sectionIdx}`} variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ pt: 2, '&:last-child': { pb: 2 } }}>
                <SectionHeader
                  title={stripLeadingDisplayJunk(section.sectionTitle)}
                  icon={<FactCheckOutlinedIcon fontSize="small" />}
                />
                <Table size="small" sx={{ mt: 1 }}>
                  <TableBody>
                    {(section.fields || []).map((field) => {
                      const pinId = docOrderFieldPinId(section.sectionKey, field.key);
                      const isPinned = Boolean(pins[pinId]);
                      const pin = pins[pinId];
                      return (
                        <TableRow
                          key={pinId}
                          hover
                          sx={{
                            ...(isPinned && {
                              bgcolor: 'rgba(var(--mui-palette-warning-mainChannel) / 0.10)',
                              boxShadow: (t) => `inset 4px 0 0 ${t.palette.warning.main}`,
                            }),
                          }}
                        >
                          <TableCell
                            sx={{
                              width: 40,
                              verticalAlign: 'top',
                              borderColor: 'divider',
                              px: 0.5,
                            }}
                          >
                            <Tooltip
                              title={
                                isPinned
                                  ? 'Remove correction flag'
                                  : 'Pin this field — add what it should be'
                              }
                            >
                              <IconButton
                                size="small"
                                color={isPinned ? 'warning' : 'default'}
                                onClick={() => togglePin(pinId)}
                                aria-label={isPinned ? 'Unpin field' : 'Pin field for correction'}
                              >
                                {isPinned ? (
                                  <PushPinIcon fontSize="small" />
                                ) : (
                                  <PushPinOutlinedIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: '32%',
                              maxWidth: 260,
                              verticalAlign: 'top',
                              fontWeight: 600,
                              color: 'text.secondary',
                              borderColor: 'divider',
                            }}
                          >
                            {stripLeadingDisplayJunk(field.label)}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top', borderColor: 'divider' }}>
                            <FieldValue value={field.value} />
                            <Collapse in={isPinned}>
                              <Stack spacing={1.5} sx={{ mt: 1.5, pl: 0.5 }}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="Should be"
                                  placeholder="Correct value for this field"
                                  value={pin?.requestedValue ?? ''}
                                  onChange={(e) =>
                                    updatePin(pinId, { requestedValue: e.target.value })
                                  }
                                />
                                <TextField
                                  size="small"
                                  fullWidth
                                  multiline
                                  minRows={2}
                                  label="Note for doc team"
                                  placeholder="Why this needs to change (optional)"
                                  value={pin?.note ?? ''}
                                  onChange={(e) => updatePin(pinId, { note: e.target.value })}
                                />
                              </Stack>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </Grid>

        {pinList.length > 0 && (
          <Grid item xs={12} lg={4}>
            <Card
              variant="outlined"
              sx={{
                position: { lg: 'sticky' },
                top: { lg: 16 },
                borderColor: 'warning.light',
                bgcolor: 'rgba(var(--mui-palette-warning-mainChannel) / 0.10)',
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <EditNoteOutlinedIcon color="warning" fontSize="small" />
                  <Typography variant="subtitle2">Correction spotlight</Typography>
                  <Chip size="small" label={pinList.length} color="warning" />
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Pinned fields queued for the doc order email.
                </Typography>
                <Stack spacing={1}>
                  {pinList.map((item) => (
                    <Box
                      key={item.pinId}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" display="block">
                        {stripLeadingDisplayJunk(item.sectionTitle)}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {stripLeadingDisplayJunk(item.field.label)}
                      </Typography>
                      {item.requestedValue?.trim() && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          → <strong>{item.requestedValue.trim()}</strong>
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Card variant="outlined" sx={{ mt: 2, bgcolor: 'background.default' }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Additional notes for the doc team
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder="Closing instructions, timing, or changes not tied to a single field…"
            value={generalNotes}
            onChange={(e) => setGeneralNotes(e.target.value)}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }} alignItems="flex-start">
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              disabled={submitting || !loanId}
              onClick={() => setConfirmOpen(true)}
            >
              Order closing documents
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ pt: 1.5, maxWidth: 420 }}>
              Submits this checklist, {pinList.length > 0 ? `${pinList.length} pinned correction(s), ` : ''}
              and your notes to docrequest@uff.loans. You receive a copy at your login email.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit doc order request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            This will email the doc order team with the current checklist
            {pinList.length > 0 ? ` and ${pinList.length} field correction(s)` : ''}
            {generalNotes.trim() ? ', plus your notes' : ''}. You are copied on the message.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} startIcon={<SendIcon />}>
            {submitting ? 'Sending…' : 'Send request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocOrderRequestPanel;
